import { Injectable } from '@angular/core';
import {
  NachaFileHeader,
  NachaBatchHeader,
  NachaRow,
  NachaFileSummary,
  NachaParseResult,
  NachaTransactionTypeLabel,
  NachaAccountType,
} from '../models/nacha.model';

/** NACHA record-type codes that represent ACH debit entries (collect money). */
const DEBIT_CODES = new Set(['27', '37', '28', '38']);
/** NACHA record-type codes that represent ACH credit entries. */
const CREDIT_CODES = new Set(['22', '32', '23', '33']);
/** All valid standard entry codes. */
const VALID_TRANSACTION_CODES = new Set([...DEBIT_CODES, ...CREDIT_CODES]);
/** SEC codes the wizard accepts. */
const VALID_SEC_CODES = new Set(['PPD', 'CCD', 'CTX', 'WEB', 'TEL']);

@Injectable({ providedIn: 'root' })
export class NachaParserService {

  readFileAsText(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve((e.target?.result as string) ?? '');
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file, 'utf-8');
    });
  }

  isDebitCode(code: string): boolean {
    return DEBIT_CODES.has(code);
  }

  transactionLabel(code: string): NachaTransactionTypeLabel {
    switch (code) {
      case '22': return 'Checking Credit';
      case '27': return 'Checking Debit';
      case '32': return 'Savings Credit';
      case '37': return 'Savings Debit';
      case '28': return 'Checking Debit Prenote';
      case '38': return 'Savings Debit Prenote';
      default:   return 'Unknown';
    }
  }

  accountTypeFromCode(code: string): NachaAccountType {
    // 2x family → CHECKING; 3x family → SAVINGS
    return code.startsWith('3') ? 'SAVINGS' : 'CHECKING';
  }

  /**
   * Strip a leading 4-digit numeric prefix from the Individual Name field.
   * The sample file consistently prefixes names with "0001" (sequence number).
   */
  private cleanIndividualName(raw: string): string {
    return raw.replace(/^\d{4}\s*/, '').trim();
  }

  parseNacha(content: string): NachaParseResult {
    const lines = content.split(/\r?\n/);
    const errors: string[] = [];
    const rows: NachaRow[] = [];
    const batches: NachaBatchHeader[] = [];
    let header: NachaFileHeader | null = null;
    let currentBatch: NachaBatchHeader | null = null;
    let lastRow: NachaRow | null = null;

    // File-level counters from record 9
    let fileTotalDebit = 0;
    let fileTotalCredit = 0;
    let fileEntryCount = 0;
    let fileBatchCount = 0;
    let destinationName = '';

    // Batch-level counters from record 8
    let batchEntryCount = 0;
    let batchDebitTotal = 0;
    let batchCreditTotal = 0;

    // Running parsed totals for validation
    let parsedEntryCount = 0;
    let parsedDebitTotal = 0;
    let parsedCreditTotal = 0;
    let parsedBatchCount = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line || line.trim() === '') continue;

      // NACHA lines should be exactly 94 characters; warn but don't skip
      if (line.length !== 94) {
        if (line.length < 94) {
          errors.push(`Line ${i + 1}: expected 94 characters, got ${line.length} — skipping`);
          continue;
        }
        // Longer than 94 – tolerate (some systems pad with spaces)
      }

      const typeCode = line[0];

      switch (typeCode) {
        case '1': {
          // File Header
          header = {
            immediateDestination: line.substring(3, 12).trim(),
            immediateOrigin: line.substring(13, 22).trim(),
            fileCreationDate: line.substring(23, 29).trim(),
            fileCreationTime: line.substring(29, 33).trim(),
            immediateDestinationName: line.substring(40, 63).trim(),
            originatorName: line.substring(63, 86).trim(),
          };
          destinationName = header.immediateDestinationName;
          break;
        }

        case '5': {
          // Batch Header
          const secCode = line.substring(50, 53).trim();
          if (!VALID_SEC_CODES.has(secCode)) {
            errors.push(`Batch at line ${i + 1}: unsupported SEC code "${secCode}". Supported: ${[...VALID_SEC_CODES].join(', ')}`);
          }
          currentBatch = {
            companyName: line.substring(4, 20).trim(),
            companyId: line.substring(40, 50).trim(),
            secCode,
            companyEntryDescription: line.substring(53, 63).trim(),
            effectiveEntryDate: line.substring(69, 75).trim(),
            batchNumber: line.substring(87, 94).trim(),
          };
          batches.push(currentBatch);
          parsedBatchCount++;
          batchEntryCount = 0;
          batchDebitTotal = 0;
          batchCreditTotal = 0;
          break;
        }

        case '6': {
          // Entry Detail
          const transactionCode = line.substring(1, 3).trim();
          const routingNumber = line.substring(3, 12).trim();
          const bankAccountNumber = line.substring(12, 29).trim();
          const amountStr = line.substring(29, 39).trim();
          const individualIdNumber = line.substring(39, 54).trim();
          const rawIndividualName = line.substring(54, 76).trim();
          const addendaRecordIndicator = line[78] ?? '0';
          const traceNumber = line.substring(79, 94).trim();

          const amountCents = parseInt(amountStr, 10) || 0;
          const amount = amountCents / 100;
          const isDebit = this.isDebitCode(transactionCode);

          const nachaRow: NachaRow = {
            rowIndex: rows.length + 1,
            transactionCode,
            transactionTypeLabel: this.transactionLabel(transactionCode),
            isDebit,
            accountType: this.accountTypeFromCode(transactionCode),
            routingNumber,
            bankAccountNumber,
            amountCents,
            amount,
            individualIdNumber,
            rawIndividualName,
            individualName: this.cleanIndividualName(rawIndividualName),
            addendaRecordIndicator,
            traceNumber,
            batchNumber: currentBatch?.batchNumber,
          };

          rows.push(nachaRow);
          lastRow = nachaRow;
          parsedEntryCount++;
          batchEntryCount++;

          if (isDebit || DEBIT_CODES.has(transactionCode)) {
            parsedDebitTotal += amountCents;
            batchDebitTotal += amountCents;
          } else if (CREDIT_CODES.has(transactionCode)) {
            parsedCreditTotal += amountCents;
            batchCreditTotal += amountCents;
          }
          break;
        }

        case '7': {
          // Addenda
          if (lastRow) {
            const addendaInfo = line.substring(3, 83).trim();
            lastRow.addendaInfo = addendaInfo;
          }
          parsedEntryCount++;
          batchEntryCount++;
          break;
        }

        case '8': {
          // Batch Control — validate batch totals
          const ctrlEntryCount = parseInt(line.substring(4, 10).trim(), 10) || 0;
          const ctrlDebit = parseInt(line.substring(20, 32).trim(), 10) || 0;
          const ctrlCredit = parseInt(line.substring(32, 44).trim(), 10) || 0;

          if (ctrlEntryCount !== batchEntryCount) {
            errors.push(`Batch ${currentBatch?.batchNumber}: entry count mismatch — expected ${ctrlEntryCount}, found ${batchEntryCount}`);
          }
          if (ctrlDebit !== batchDebitTotal) {
            errors.push(`Batch ${currentBatch?.batchNumber}: debit total mismatch — expected ${ctrlDebit}, found ${batchDebitTotal}`);
          }
          if (ctrlCredit !== batchCreditTotal) {
            errors.push(`Batch ${currentBatch?.batchNumber}: credit total mismatch — expected ${ctrlCredit}, found ${batchCreditTotal}`);
          }
          currentBatch = null;
          lastRow = null;
          break;
        }

        case '9': {
          // File Control
          fileBatchCount = parseInt(line.substring(1, 7).trim(), 10) || 0;
          fileEntryCount = parseInt(line.substring(13, 21).trim(), 10) || 0;
          fileTotalDebit = parseInt(line.substring(31, 43).trim(), 10) || 0;
          fileTotalCredit = parseInt(line.substring(43, 55).trim(), 10) || 0;
          break;
        }
      }
    }

    // File-level validation (only when file control record was found)
    if (fileBatchCount > 0 && fileBatchCount !== parsedBatchCount) {
      errors.push(`File batch count mismatch — control: ${fileBatchCount}, parsed: ${parsedBatchCount}`);
    }
    if (fileEntryCount > 0 && fileEntryCount !== parsedEntryCount) {
      errors.push(`File entry count mismatch — control: ${fileEntryCount}, parsed: ${parsedEntryCount}`);
    }
    if (fileTotalDebit > 0 && fileTotalDebit !== parsedDebitTotal) {
      errors.push(`File debit total mismatch — control: ${fileTotalDebit}, parsed: ${parsedDebitTotal}`);
    }
    if (fileTotalCredit > 0 && fileTotalCredit !== parsedCreditTotal) {
      errors.push(`File credit total mismatch — control: ${fileTotalCredit}, parsed: ${parsedCreditTotal}`);
    }

    // Entry-only rows (type 6) that are valid debit/credit codes
    const entryRows = rows.filter((r) => VALID_TRANSACTION_CODES.has(r.transactionCode));

    const summary: NachaFileSummary = {
      originatorName: header?.originatorName ?? '',
      destinationName,
      fileCreationDate: header?.fileCreationDate ?? '',
      batchCount: parsedBatchCount,
      blockCount: Math.ceil(lines.filter((l) => l.trim() !== '').length / 10),
      entryCount: entryRows.length,
      totalDebitAmount: parsedDebitTotal / 100,
      totalCreditAmount: parsedCreditTotal / 100,
      secCodes: [...new Set(batches.map((b) => b.secCode).filter(Boolean))],
      validationErrors: errors,
      isValid: errors.length === 0 && rows.length > 0,
    };

    return { header, batches, rows: entryRows, summary, errors };
  }
}
